import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Spinner,
} from "@getgreenline/blaze-ui";
import { Plus, SearchX, TriangleAlert, UsersRound } from "lucide-react";
import { useState } from "react";
import { AddBrandDialog } from "@/components/backoffice/add-brand-dialog";
import { AssignBrandUserDialog } from "@/components/backoffice/assign-brand-user-dialog";
import { BrandUsersTable } from "@/components/backoffice/brand-users-table";
import { StaffAccessGate } from "@/components/shared/staff-access-gate";
import { useOnboardedBrands } from "@/hooks/use-staff-brand-users";
import type { OnboardedBrand } from "@/services/staff-brand-users.service";

function BrandsState({
  query,
  brands,
  isLoading,
  isError,
  onAssignUser,
}: {
  query: string;
  brands: OnboardedBrand[];
  isLoading: boolean;
  isError: boolean;
  onAssignUser: (brand: OnboardedBrand) => void;
}) {
  const normalizedQuery = query.trim().toLowerCase();
  const filteredBrands = normalizedQuery
    ? brands.filter(
        (brand) =>
          brand.name.toLowerCase().includes(normalizedQuery) ||
          brand.owner?.email.toLowerCase().includes(normalizedQuery) ||
          brand.users.some((user) =>
            user.email.toLowerCase().includes(normalizedQuery),
          ),
      )
    : brands;

  if (isLoading) {
    return (
      <Empty className="tw:border">
        <EmptyMedia>
          <Spinner className="tw:size-8" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>Loading onboarded brands</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <TriangleAlert className="tw:size-4" />
        <AlertTitle>Onboarded brands could not be loaded</AlertTitle>
        <AlertDescription>
          Try again. If the problem continues, contact support.
        </AlertDescription>
      </Alert>
    );
  }

  if (brands.length === 0) {
    return (
      <Empty className="tw:border">
        <EmptyMedia variant="icon">
          <UsersRound className="tw:size-5" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>No onboarded brands</EmptyTitle>
        </EmptyHeader>
        <EmptyContent>
          Brands will appear here after their first user is assigned.
        </EmptyContent>
      </Empty>
    );
  }

  if (filteredBrands.length === 0) {
    return (
      <Empty className="tw:border">
        <EmptyMedia variant="icon">
          <SearchX className="tw:size-5" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>No matching brands</EmptyTitle>
        </EmptyHeader>
        <EmptyContent>Try a different brand or user email.</EmptyContent>
      </Empty>
    );
  }

  return (
    <BrandUsersTable brands={filteredBrands} onAssignUser={onAssignUser} />
  );
}

export function BackofficePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<OnboardedBrand | null>(
    null,
  );
  const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
  const onboardedBrands = useOnboardedBrands();
  const brands = onboardedBrands.data ?? [];

  return (
    <StaffAccessGate>
      <main className="tw:@container tw:flex tw:min-w-0 tw:flex-col tw:gap-6 tw:px-6 tw:py-8 sm:tw:px-8">
        <CardHeader className="tw:px-0 tw:pt-0">
          <CardTitle>
            <h1 className="tw:text-2xl tw:font-semibold">Brands</h1>
          </CardTitle>
          <CardDescription className="tw:max-w-2xl">
            View brands with assigned users and add people to their accounts.
          </CardDescription>
        </CardHeader>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:w-full tw:max-w-xl sm:tw:w-auto sm:tw:flex-1">
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search brands or users"
              aria-label="Search onboarded brands"
            />
          </div>
          <Button type="button" onClick={() => setIsAddBrandOpen(true)}>
            <Plus className="tw:size-4" />
            Add brand
          </Button>
        </div>

        <BrandsState
          query={searchQuery}
          brands={brands}
          isLoading={onboardedBrands.isLoading}
          isError={onboardedBrands.isError}
          onAssignUser={setSelectedBrand}
        />

        {selectedBrand ? (
          <AssignBrandUserDialog
            brand={selectedBrand}
            open
            onOpenChange={(open) => {
              if (!open) setSelectedBrand(null);
            }}
          />
        ) : null}

        {isAddBrandOpen ? (
          <AddBrandDialog
            onboardedBrandIds={brands.map((brand) => brand.id)}
            open
            onOpenChange={setIsAddBrandOpen}
          />
        ) : null}
      </main>
    </StaffAccessGate>
  );
}
